import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Settings, ArrowLeft, Loader2, Save, Edit3 } from 'lucide-react';
import { StatusConfiguration } from './StatusConfiguration';
import { ReportColumnConfigModal } from './ReportColumnConfigModal';
import type { DashboardProject } from '../../../types/project';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';

interface StatusConfigurationModalProps {
  project: DashboardProject;
  selectedPipeSize: number;
  selectedPipe: any;
  isOpen: boolean;
  onClose: () => void;
  onStatusConfigSaved?: (savedAt: number) => void;
  canEdit?: boolean;
}

export const StatusConfigurationModal: React.FC<StatusConfigurationModalProps> = ({
  project,
  selectedPipeSize,
  selectedPipe,
  isOpen,
  onClose,
  onStatusConfigSaved,
  canEdit = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [triggerSave, setTriggerSave] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [hasModifications, setHasModifications] = useState(false);
  const [isColumnConfigModalOpen, setIsColumnConfigModalOpen] = useState(false);
  const [unitsRefreshToken, setUnitsRefreshToken] = useState(0);
  const [statusEnabledParameters, setStatusEnabledParameters] = useState<Record<string, boolean>>({});

  const projectId = project?.project?.projectId
    ? Number(project.project.projectId)
    : undefined;
  const projectName = project?.project?.projectName ?? null;

  useProductionModalAudit(isOpen, 'Status Configuration', projectId, projectName);

  const handleSave = () => {
    setIsSaving(true);
    setTriggerSave(true);
    setTimeout(() => setTriggerSave(false), 100);
  };

  const handleSaveComplete = () => {
    setIsSaving(false);
    onStatusConfigSaved?.(Date.now());
    setTimeout(() => setIsSaving(false), 100);
  };

  const handleValidationFailure = () => {
    setIsSaving(false);
  };

  const handleValidationErrorsChange = (hasErrors: boolean) => {
    setHasValidationErrors(hasErrors);
  };

  const handleColumnConfigSave = () => {
    setUnitsRefreshToken((token) => token + 1);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-y-auto overflow-x-hidden">
          <div className="flex flex-col min-h-full">
            <DialogHeader className="flex-shrink-0 px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                >
                  <ArrowLeft className="h-4 w-4 text-text-grey" />
                </Button>
                <div>
                  <h2 className="text-[18px] font-semibold text-blueprint-blue leading-tight">Status Configuration</h2>
                  <p className="text-[13px] text-heritage-grey mt-0.5">Project: {project.project.projectName}</p>
                </div>
                {hasValidationErrors && !isSaving && (
                  <div className="text-[12px] text-readable-red ml-2">Validation errors detected</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setIsColumnConfigModalOpen(true)}
                  size="sm"
                  variant="outline"
                  className="h-9 px-3 text-xs"
                  disabled={!canEdit || isSaving}
                >
                  <Edit3 className="h-3 w-3 mr-1" />
                  Configure Units
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!canEdit || isSaving || !hasModifications}
                  className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 min-h-0 bg-page-bg">
              <StatusConfiguration
                project={project}
                selectedPipeSize={selectedPipeSize}
                selectedPipe={selectedPipe}
                isEditing={true}
                setIsEditing={setIsEditing}
                onSave={() => {}}
                onSaveComplete={handleSaveComplete}
                triggerSave={triggerSave}
                onValidationErrorsChange={handleValidationErrorsChange}
                onValidationFailure={handleValidationFailure}
                onDirtyChange={setHasModifications}
                isModalOpen={isOpen}
                canEdit={canEdit}
                unitsRefreshToken={unitsRefreshToken}
                onEnabledParameterKeysChange={setStatusEnabledParameters}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReportColumnConfigModal
        isOpen={isColumnConfigModalOpen}
        onClose={() => setIsColumnConfigModalOpen(false)}
        onSave={handleColumnConfigSave}
        projectId={projectId}
        projectName={projectName}
        mode="status"
        statusEnabledParameters={statusEnabledParameters}
      />
    </>
  );
};

export const StatusConfigurationButton: React.FC<{
  project: DashboardProject;
  selectedPipeSize: number;
  selectedPipe: any;
  onOpen: () => void;
}> = ({ onOpen }) => {
  return (
    <Button onClick={onOpen} className="bg-red-600 hover:bg-red-700 flex items-center gap-2">
      <Settings className="h-4 w-4" />
      Status Configuration
    </Button>
  );
};
